import { useEffect, useRef } from 'react';
import './index-ambient-effects.css';

export const INDEX_AMBIENT_EFFECT_CODES = [
  'cinematic-rain', 'velvet-snow', 'love-ember', 'golden-hour-bokeh', 'aurora-borealis',
  'floating-dust-motes', 'neon-city-haze', 'ambient-fireflies', 'steam-mist-rise',
  'confetti-romance', 'starry-night-twinkle', 'sunset-gradient-shift', 'water-caustics',
  'cherry-blossom-petals', 'candle-flicker-glow', 'magic-sparkle-trail', 'foggy-window',
  'holographic-iridescence', 'heartbeat-pulse', 'floating-lanterns',
] as const;

export type IndexAmbientEffectCode = (typeof INDEX_AMBIENT_EFFECT_CODES)[number];
type StyleValue = string | number;
type Builder = (layer: HTMLDivElement) => void;

const random = (a: number, b: number) => a + Math.random() * (b - a);
const randomInt = (a: number, b: number) => Math.floor(random(a, b + 1));
const pick = <T,>(values: readonly T[]) => values[randomInt(0, values.length - 1)];

function particle(className: string, styles: Record<string, StyleValue>, parent: HTMLElement): HTMLDivElement {
  const node = document.createElement('div');
  node.className = `mfx-index-p ${className}`.trim();
  Object.assign(node.style, styles);
  parent.appendChild(node);
  return node;
}

function setVar(node: HTMLElement, name: string, value: StyleValue) {
  node.style.setProperty(name, String(value));
}

const builders: Record<IndexAmbientEffectCode, Builder> = {
  'cinematic-rain': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-rain-glow' }));
    for (let i = 0; i < 110; i += 1) {
      const far = i > 70;
      particle(`mfx-index-rain-drop${far ? ' mfx-index-rain-blur' : ''}`, {
        left: `${random(-10, 105)}%`,
        height: `${random(far ? 18 : 26, far ? 34 : 70)}px`,
        opacity: far ? 0.35 : random(0.45, 0.95),
        animationDuration: `${random(far ? 1.5 : 0.55, far ? 2.2 : 1)}s`,
        animationDelay: `${-random(0, 2.5)}s`,
      }, layer);
    }
    for (let i = 0; i < 16; i += 1) {
      particle('mfx-index-ripple', {
        left: `${random(2, 92)}%`,
        top: `${random(62, 97)}%`,
        animationDuration: `${random(1.3, 2.3)}s`,
        animationDelay: `${-random(0, 3)}s`,
      }, layer);
    }
  },
  'velvet-snow': (layer) => {
    const layers = [
      { count: 40, size: [1.5, 3], blur: 2.6, opacity: 0.35, duration: [16, 24] },
      { count: 45, size: [3, 5], blur: 1.1, opacity: 0.6, duration: [11, 17] },
      { count: 35, size: [5, 9], blur: 0, opacity: 0.95, duration: [7, 12] },
    ];
    layers.forEach((config) => {
      const wrap = document.createElement('div');
      wrap.className = 'mfx-index-snow-layer';
      wrap.style.filter = `blur(${config.blur}px)`;
      wrap.style.opacity = String(config.opacity);
      layer.appendChild(wrap);
      for (let i = 0; i < config.count; i += 1) {
        const size = random(config.size[0], config.size[1]);
        particle('mfx-index-flake', {
          left: `${random(-5, 102)}%`, width: `${size}px`, height: `${size}px`,
          animationDuration: `${random(config.duration[0], config.duration[1])}s`,
          animationDelay: `${-random(0, 20)}s`, top: `${-random(0, 300)}px`,
        }, wrap);
      }
    });
  },
  'love-ember': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-ember-heat' }));
    const colors = ['#ff5864', '#ff7854', '#ff9a7a', '#ffc46b', '#ff3f6e'];
    for (let i = 0; i < 80; i += 1) {
      const size = random(6, 20);
      const node = particle('mfx-index-heart', {
        left: `${random(-2, 100)}%`, top: `${random(88, 112)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(6, 13)}s`, animationDelay: `${-random(0, 13)}s`, opacity: random(0.5, 1),
      }, layer);
      setVar(node, '--mfx-heart-color', pick(colors));
    }
    for (let i = 0; i < 30; i += 1) {
      const size = random(1.5, 3.5);
      particle('mfx-index-spark', {
        left: `${random(0, 100)}%`, top: `${random(90, 108)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(5, 10)}s`, animationDelay: `${-random(0, 10)}s`,
      }, layer);
    }
  },
  'golden-hour-bokeh': (layer) => {
    const colors = ['rgba(255,196,110,.55)', 'rgba(255,140,90,.45)', 'rgba(255,220,170,.5)', 'rgba(255,110,120,.4)', 'rgba(255,175,60,.42)'];
    for (let i = 0; i < 46; i += 1) {
      const size = random(30, 150);
      const node = particle('mfx-index-bokeh', {
        left: `${random(-10, 100)}%`, top: `${random(-5, 100)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(14, 30)}s`, animationDelay: `${-random(0, 25)}s`,
      }, layer);
      setVar(node, '--mfx-bokeh-color', pick(colors));
      setVar(node, '--mfx-bokeh-blur', `${size / 12}px`);
      setVar(node, '--mfx-bokeh-opacity', random(0.35, 0.85));
      setVar(node, '--mfx-dx', `${random(-90, 90)}px`);
      setVar(node, '--mfx-dy', `${random(-200, -60)}px`);
    }
  },
  'aurora-borealis': (layer) => {
    const gradients = [
      'linear-gradient(90deg,transparent,rgba(60,255,190,.6),rgba(40,200,255,.4),transparent)',
      'linear-gradient(90deg,transparent,rgba(150,90,255,.5),rgba(60,255,200,.45),transparent)',
      'linear-gradient(90deg,transparent,rgba(255,90,180,.35),rgba(90,160,255,.5),transparent)',
      'linear-gradient(90deg,transparent,rgba(90,255,230,.45),transparent)',
    ];
    gradients.forEach((background, index) => {
      const node = document.createElement('div');
      node.className = 'mfx-index-aurora-band';
      node.style.cssText = `background:${background};top:${4 + index * 15}%;animation-duration:${14 + index * 5}s;animation-delay:${-index * 3}s;opacity:${0.85 - index * 0.12}`;
      layer.appendChild(node);
    });
    for (let i = 0; i < 70; i += 1) {
      particle('mfx-index-aurora-star', {
        left: `${random(0, 100)}%`, top: `${random(0, 100)}%`,
        animationDuration: `${random(2.5, 7)}s`, animationDelay: `${-random(0, 7)}s`,
      }, layer);
    }
  },
  'floating-dust-motes': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-dust-beam' }));
    for (let i = 0; i < 130; i += 1) {
      const size = random(1, 4);
      particle('mfx-index-mote', {
        left: `${random(0, 100)}%`, top: `${random(0, 110)}%`, width: `${size}px`, height: `${size}px`,
        opacity: random(0.25, 0.95), animationDuration: `${random(7, 20)}s`, animationDelay: `${-random(0, 18)}s`,
      }, layer);
    }
  },
  'neon-city-haze': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-neon-haze' }));
    const colors = ['#ff2878', '#28beff', '#a03cff', '#ff7a2f', '#3cffd0'];
    for (let i = 0; i < 70; i += 1) {
      const color = pick(colors);
      particle('mfx-index-streak', {
        left: `${random(0, 100)}%`, height: `${random(50, 220)}px`, width: `${random(1, 3)}px`, opacity: random(0.4, 1),
        background: `linear-gradient(180deg,transparent,${color},transparent)`,
        animationDuration: `${random(2.4, 6.5)}s`, animationDelay: `${-random(0, 6)}s`,
      }, layer);
    }
  },
  'ambient-fireflies': (layer) => {
    for (let i = 0; i < 48; i += 1) {
      const size = random(2.5, 5.5);
      particle('mfx-index-firefly', {
        left: `${random(2, 96)}%`, top: `${random(5, 95)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(9, 20)}s, ${random(1.6, 4.5)}s`,
        animationDelay: `${-random(0, 18)}s, ${-random(0, 4)}s`,
      }, layer);
    }
  },
  'steam-mist-rise': (layer) => {
    for (let i = 0; i < 34; i += 1) {
      const size = random(70, 220);
      const node = particle('mfx-index-mist-blob', {
        left: `${random(-15, 95)}%`, top: `${random(55, 105)}%`, width: `${size}px`, height: `${size * random(0.6, 1)}px`,
        animationDuration: `${random(14, 30)}s`, animationDelay: `${-random(0, 28)}s`, opacity: 0,
      }, layer);
      setVar(node, '--mfx-dx', `${random(-70, 70)}px`);
    }
  },
  'confetti-romance': (layer) => {
    const colors = ['#FFC9A3', '#E8A87C', '#FF5864', '#FFD9C0', '#F7B267', '#FF9AAE', '#FFE8D6'];
    for (let i = 0; i < 95; i += 1) {
      const width = random(5, 12);
      const tall = Math.random() > 0.5;
      const node = particle('mfx-index-confetti', {
        left: `${random(-5, 102)}%`, width: `${width}px`, height: `${tall ? width * random(1.4, 2.4) : width}px`,
        background: `linear-gradient(140deg,${pick(colors)},${pick(colors)})`,
        borderRadius: Math.random() > 0.6 ? '50%' : '1.5px', boxShadow: '0 0 6px rgba(255,190,150,.35)',
        animationDuration: `${random(4.5, 11)}s`, animationDelay: `${-random(0, 11)}s`, top: '-60px',
      }, layer);
      setVar(node, '--mfx-dx', `${random(-120, 120)}px`);
    }
  },
  'starry-night-twinkle': (layer) => {
    for (let i = 0; i < 140; i += 1) {
      const size = random(0.8, 2.8);
      particle('mfx-index-star', {
        left: `${random(0, 100)}%`, top: `${random(0, 100)}%`, width: `${size}px`, height: `${size}px`,
        boxShadow: size > 2 ? '0 0 6px rgba(200,225,255,.9)' : 'none',
        animationDuration: `${random(2, 6)}s`, animationDelay: `${-random(0, 6)}s`,
      }, layer);
    }
    for (let i = 0; i < 4; i += 1) {
      particle('mfx-index-shooting-star', {
        left: `${random(-5, 50)}%`, top: `${random(2, 40)}%`, animationDuration: `${random(7, 13)}s`, animationDelay: `${-random(0, 12)}s`,
      }, layer);
    }
  },
  'sunset-gradient-shift': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-sun-orb' }));
    for (let i = 0; i < 40; i += 1) {
      const size = random(2, 6);
      particle('mfx-index-mote', {
        left: `${random(0, 100)}%`, top: `${random(20, 100)}%`, width: `${size}px`, height: `${size}px`, opacity: random(0.15, 0.5),
        animationDuration: `${random(10, 22)}s`, animationDelay: `${-random(0, 20)}s`,
      }, layer);
    }
  },
  'water-caustics': (layer) => {
    const first = document.createElement('div'); first.className = 'mfx-index-caustics-layer'; layer.appendChild(first);
    const second = document.createElement('div'); second.className = 'mfx-index-caustics-layer mfx-index-caustics-layer--soft'; layer.appendChild(second);
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-caustics-shim' }));
    for (let i = 0; i < 30; i += 1) {
      const size = random(2, 5);
      particle('mfx-index-mote mfx-index-water-mote', {
        left: `${random(0, 100)}%`, top: `${random(0, 100)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(9, 18)}s`, animationDelay: `${-random(0, 16)}s`,
      }, layer);
    }
  },
  'cherry-blossom-petals': (layer) => {
    for (let i = 0; i < 80; i += 1) {
      const width = random(8, 17);
      const node = particle('mfx-index-petal', {
        left: `${random(-5, 100)}%`, width: `${width}px`, height: `${width * random(0.62, 0.85)}px`,
        animationDuration: `${random(7, 16)}s, ${random(2, 4.5)}s`, animationDelay: `${-random(0, 15)}s, ${-random(0, 4)}s`,
        top: '-60px', opacity: random(0.6, 1),
      }, layer);
      setVar(node, '--mfx-dx', `${random(-110, 110)}px`);
    }
  },
  'candle-flicker-glow': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-candle-core mfx-index-candle-core--wide' }));
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-candle-core' }));
    for (let i = 0; i < 34; i += 1) {
      const size = random(1.5, 4);
      particle('mfx-index-spark', {
        left: `${random(28, 72)}%`, top: `${random(55, 80)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(4, 9)}s`, animationDelay: `${-random(0, 9)}s`,
      }, layer);
    }
  },
  'magic-sparkle-trail': (layer) => {
    const colors = ['#ffffff', '#ffd8f5', '#c9b6ff', '#fff0b8', '#a8e9ff'];
    for (let i = 0; i < 120; i += 1) {
      const size = random(5, 16);
      const node = particle('mfx-index-sparkle', {
        left: `${random(0, 100)}%`, top: `${random(0, 100)}%`, width: `${size}px`, height: `${size}px`,
        animationDuration: `${random(1.6, 4)}s`, animationDelay: `${-random(0, 4)}s`,
      }, layer);
      setVar(node, '--mfx-sparkle-color', pick(colors));
    }
  },
  'foggy-window': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-fog-mask' }));
    for (let i = 0; i < 10; i += 1) {
      const size = random(90, 240);
      const node = document.createElement('div');
      node.className = 'mfx-index-fog-cloud';
      node.style.cssText = `left:${random(-10, 90)}%;top:${random(-10, 88)}%;width:${size}px;height:${size * random(0.6, 1)}px;animation-duration:${random(9, 20)}s;animation-delay:${-random(0, 10)}s;opacity:${random(0.3, 0.8)}`;
      layer.appendChild(node);
    }
    for (let i = 0; i < 34; i += 1) {
      particle('mfx-index-trickle', {
        left: `${random(2, 97)}%`, height: `${random(10, 34)}px`, width: `${random(3, 7)}px`,
        animationDuration: `${random(5, 16)}s`, animationDelay: `${-random(0, 15)}s`,
      }, layer);
    }
  },
  'holographic-iridescence': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-holo-mesh' }));
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-holo-sheen' }));
  },
  'heartbeat-pulse': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-beat-vignette' }));
    for (let i = 0; i < 4; i += 1) {
      const ring = document.createElement('div');
      ring.className = 'mfx-index-beat-ring';
      ring.style.animationDelay = `${i * 0.575}s`;
      layer.appendChild(ring);
    }
    for (let i = 0; i < 40; i += 1) {
      const size = random(5, 14);
      const node = particle('mfx-index-heart', {
        left: `${random(0, 100)}%`, top: `${random(85, 110)}%`, width: `${size}px`, height: `${size}px`, opacity: random(0.2, 0.6),
        animationDuration: `${random(7, 14)}s`, animationDelay: `${-random(0, 14)}s`,
      }, layer);
      setVar(node, '--mfx-heart-color', '#ff3248');
    }
  },
  'floating-lanterns': (layer) => {
    layer.appendChild(Object.assign(document.createElement('div'), { className: 'mfx-index-lantern-glow' }));
    for (let i = 0; i < 70; i += 1) {
      particle('mfx-index-star', {
        left: `${random(0, 100)}%`, top: `${random(0, 70)}%`, width: '1.6px', height: '1.6px',
        animationDuration: `${random(3, 7)}s`, animationDelay: `${-random(0, 7)}s`,
      }, layer);
    }
    for (let i = 0; i < 28; i += 1) {
      const width = random(12, 36);
      const depth = width / 36;
      const node = particle('mfx-index-lantern', {
        left: `${random(-5, 98)}%`, top: `${random(70, 120)}%`, width: `${width}px`, height: `${width * 1.35}px`,
        filter: depth < 0.5 ? 'blur(1.6px)' : 'none', opacity: random(0.55, 1),
        animationDuration: `${random(16, 34)}s, ${random(3.5, 7)}s`, animationDelay: `${-random(0, 30)}s, ${-random(0, 6)}s`,
      }, layer);
      setVar(node, '--mfx-dx', `${random(-90, 90)}px`);
    }
  },
};

export function IndexAmbientOverlay({
  code,
  intensity = 0.6,
  reducedMotion = false,
}: {
  code: string;
  intensity?: number;
  reducedMotion?: boolean;
}) {
  const layerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return undefined;
    layer.replaceChildren();
    const builder = builders[code as IndexAmbientEffectCode];
    if (builder) builder(layer);
    return () => layer.replaceChildren();
  }, [code]);

  if (!INDEX_AMBIENT_EFFECT_CODES.includes(code as IndexAmbientEffectCode)) return null;
  const opacity = Math.max(0, Math.min(1, intensity));
  return (
    <div
      ref={layerRef}
      className={`mfx-index-ambient${reducedMotion ? ' mfx-index-ambient--reduced' : ''}`}
      data-effect={code}
      aria-hidden="true"
      style={{ opacity }}
    />
  );
}

export { builders as indexAmbientBuilders };
